import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import Text from '@atoms/Text/Text';
import ThemeEnum from '@models/enums/ThemeEnum';
import { IImage, IImageWithAltText } from '@models/IImage';
import ProductCardImage from '@molecules/ProductCardImage/ProductCardImage';
import { fadeIn } from '@utility/config/animations';
import { brightnessByColour } from '@utility/helpers/brightnessByColour';
import { handleHexCode } from '@utility/helpers/handleHexCode';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import { CSSProperties, useState } from 'react';
import { useSwipeable } from 'react-swipeable';
import { twMerge } from 'tailwind-merge';

export interface IProductImageGallery {
  readonly colourHexCode?: string;
  readonly colourSpecialImage?: string;
  readonly imageAspectRatio?: string;
  readonly images: IImageWithAltText[] | IImage[];
  readonly initialIndex?: number;
  readonly isCard?: boolean;
  readonly lazyLoadProductImage?: boolean;
  readonly mobileFirstImage?: IImage;
  readonly productImageClasses?: string;
  readonly showCarouselDots?: boolean;
  readonly showColourImage?: boolean;
  readonly showLoyaltyImage?: boolean;
  readonly showProductImage?: boolean;
  readonly swipeable?: boolean;
  readonly tags?: string[];
  readonly title?: string;
  readonly trumpetText?: string;
}

const isIImageWithAltTextArray = (
  images: IImageWithAltText[] | IImage[]
): images is IImageWithAltText[] => !!images?.[0] && 'image' in images[0];

const ProductImageGallery = ({
  colourHexCode,
  colourSpecialImage,
  imageAspectRatio = 'tw-aspect-[6/7]',
  images,
  initialIndex,
  isCard = true,
  lazyLoadProductImage = true,
  mobileFirstImage,
  showCarouselDots = false,
  showColourImage = false,
  showLoyaltyImage = false,
  showProductImage = false,
  swipeable = false,
  tags,
  title,
  trumpetText,
}: IProductImageGallery) => {
  const [activeIndex, setActiveIndex] = useState<number>(initialIndex || 0);
  const [dragOffset, setDragOffset] = useState<number>(0);

  const numberOfSlides =
    showProductImage || showColourImage || showLoyaltyImage
      ? images.length
      : images.length - 1;

  const showMobileImage =
    showProductImage || showColourImage || showLoyaltyImage;

  const swiperHandlers = useSwipeable({
    onSwiped: () => setDragOffset(0),
    onSwipedLeft: () => requestAnimationFrame(goToNext),
    onSwipedRight: () => requestAnimationFrame(goToPrev),
    onSwiping: (event) => {
      if (event.dir === 'Left' || event.dir === 'Right') {
        setDragOffset(event.deltaX * -1);
      }
    },
  });

  const handlers = swipeable ? swiperHandlers : {};

  if (!title && !images?.length && !showLoyaltyImage) return null;

  const translateX = `calc(${activeIndex * 100}% + ${dragOffset}px)`;

  const isBright =
    colourHexCode &&
    title &&
    brightnessByColour(handleHexCode(colourHexCode)) > 127;

  // Always show dark text for products and light text for colours unless there's a hex code and a title where we determine based on colour brightness
  const textColour = showProductImage
    ? ThemeEnum.Dark
    : isBright
    ? ThemeEnum.Dark
    : ThemeEnum.Light;

  const renderImages = () => (
    <ol
      dir="ltr"
      className={twMerge(
        'tw-flex tw-h-full tw--translate-x-[--offset] tw-flex-row tw-transition-transform tw-duration-[1s] tw-ease-in-out-quart tw-will-change-transform',
        dragOffset === 0 ? 'tw-transition-transform' : ''
      )}
      style={{ '--offset': translateX } as CSSProperties}
    >
      {(mobileFirstImage || colourHexCode || colourSpecialImage) &&
        showMobileImage && (
          <li
            className={classNames(
              'tw-relative tw-h-full tw-w-full tw-shrink-0 tw-grow-0 tw-basis-full tw-bg-grey-10',
              imageAspectRatio
            )}
          >
            {showLoyaltyImage && mobileFirstImage && (
              <Image
                image={mobileFirstImage}
                classes="tw-h-full"
                imageClasses="tw-h-full tw-object-cover"
              />
            )}

            {showProductImage && mobileFirstImage && (
              <ProductCardImage
                productImage={mobileFirstImage}
                tags={tags}
                lazyLoad={lazyLoadProductImage}
              />
            )}

            {(colourHexCode || colourSpecialImage) && (
              <div
                className="tw-absolute tw-flex tw-h-full tw-w-full tw-items-center tw-justify-center"
                style={{
                  backgroundImage: colourSpecialImage
                    ? `url(${colourSpecialImage})`
                    : 'none',
                  backgroundColor: colourSpecialImage
                    ? 'unset'
                    : `#${colourHexCode}`,
                }}
              />
            )}
          </li>
        )}

      {images.map((image, index) => {
        const img = isIImageWithAltTextArray(images)
          ? (image as IImageWithAltText).image
          : (image as IImage);
        const alt =
          isIImageWithAltTextArray(images) &&
          (image as IImageWithAltText).alternativeText
            ? (image as IImageWithAltText).alternativeText
            : img.altText;

        return (
          <li
            key={`${alt || 'image'}-${index}`}
            className={classNames(
              'tw-relative tw-h-full tw-w-full tw-shrink-0 tw-grow-0 tw-basis-full tw-bg-grey-10',
              imageAspectRatio
            )}
          >
            <Image
              image={img}
              alternativeText={alt}
              aspectRatio={0.8}
              imageClasses="tw-absolute tw-inset-0 tw-z-10 tw-w-full tw-h-full tw-object-center tw-object-cover"
              sizes={isCard ? '20vw' : '100vw'}
            />
          </li>
        );
      })}
    </ol>
  );

  return (
    <div
      {...handlers}
      className="tw-relative tw-h-full tw-touch-pan-y tw-overflow-hidden"
      onKeyDown={(event) => {
        if (['ArrowRight', 'ArrowLeft'].includes(event.key)) {
          event.preventDefault();
          event.stopPropagation();
          event.key === 'ArrowRight' ? goToNext() : goToPrev();
        }
      }}
    >
      <AnimatePresence>
        {title && activeIndex === 0 && (
          <motion.div
            initial="visible"
            animate="visible"
            exit="hidden"
            variants={fadeIn}
            className={classNames(
              'tw-absolute tw-z-10 tw-flex tw-h-full tw-w-full tw-flex-col tw-items-center tw-gap-xxs tw-px-xs tw-text-center',
              showProductImage && mobileFirstImage
                ? 'tw-justify-start tw-pt-[58px] md:tw-pt-[100px]'
                : 'tw-justify-center'
            )}
          >
            {trumpetText && (
              <Text
                className={classNames(
                  'tw-block tw-uppercase',
                  textColour === ThemeEnum.Dark
                    ? 'tw-text-grey-70'
                    : 'tw-text-grey-30'
                )}
                variant="body-small"
              >
                {trumpetText}
              </Text>
            )}
            <Text
              variant="hero-headline"
              as="h1"
              className={classNames(
                'tw-capitalize',
                textColour === ThemeEnum.Dark
                  ? 'tw-text-black'
                  : 'tw-text-white'
              )}
            >
              {title}
            </Text>
          </motion.div>
        )}
      </AnimatePresence>
      <div className="tw-h-full">{renderImages()}</div>
      <AnimatePresence>
        {(!showMobileImage || (showMobileImage && activeIndex > 0)) && (
          <motion.div
            key="image-overlay"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={fadeIn}
            className={classNames(
              'before:tw-absolute before:tw-left-0 before:tw-top-0 before:tw-h-full before:tw-w-[60px] before:tw-bg-gradient-to-r before:tw-from-black/20 before:tw-mix-blend-multiply after:tw-absolute after:tw-right-0 after:tw-top-0 after:tw-h-full after:tw-w-[60px] after:tw-bg-gradient-to-l after:tw-from-black/20 after:tw-mix-blend-multiply',
              { 'before:tw-hidden': activeIndex === 0 },
              { 'after:tw-hidden': activeIndex === numberOfSlides }
            )}
          />
        )}
      </AnimatePresence>

      <button
        type="button"
        disabled={activeIndex === 0}
        aria-disabled={activeIndex === 0}
        aria-label="Previous"
        className="tw-absolute tw-left-0 tw-top-1/2 tw-z-10 tw-m-3xs tw-block tw--translate-y-1/2 tw-rounded tw-p-3xs tw-text-white tw-transition-opacity focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-white focus:tw-ring-offset-2 focus:tw-ring-offset-grey-100 aria-disabled:tw-opacity-0"
        onClick={(event) => {
          event.preventDefault();
          goToPrev();
        }}
      >
        <Icon
          name="chevron-left"
          size="1.5rem"
          className={classNames(
            'tw-pointer-events-none',
            textColour === ThemeEnum.Dark ? 'tw-text-black' : 'tw-text-white'
          )}
        />
      </button>
      <button
        type="button"
        disabled={activeIndex === numberOfSlides}
        aria-disabled={activeIndex === numberOfSlides}
        aria-label="Next"
        className="tw-absolute tw-right-0 tw-top-1/2 tw-z-10 tw-m-3xs tw-block tw--translate-y-1/2 tw-rounded tw-p-3xs tw-text-white tw-transition-opacity focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-white focus:tw-ring-offset-2 focus:tw-ring-offset-grey-100 aria-disabled:tw-opacity-0"
        onClick={(event) => {
          event.preventDefault();
          goToNext();
        }}
      >
        <Icon
          name="chevron-right"
          size="1.5rem"
          className={classNames(
            'tw-pointer-events-none',
            textColour === ThemeEnum.Dark ? 'tw-text-black' : 'tw-text-white'
          )}
        />
      </button>

      {showCarouselDots && numberOfSlides > 0 && (
        <ol
          aria-hidden
          className={twMerge(
            'tw-absolute tw-bottom-0 tw-left-0 tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-x-2 tw-pb-sm'
          )}
        >
          {Array(numberOfSlides + 1)
            .fill('')
            .map((_, index) => (
              <li key={`product-carousel-dot-${index}`}>
                <span
                  className={classNames(
                    `tw-block tw-h-2 tw-w-2 tw-rounded-full tw-opacity-50 data-[active='true']:tw-opacity-100`,
                    textColour === ThemeEnum.Dark
                      ? 'tw-bg-black'
                      : 'tw-bg-white'
                  )}
                  data-active={index === activeIndex}
                />
              </li>
            ))}
        </ol>
      )}
    </div>
  );

  function goToNext() {
    goToIndex(activeIndex + 1);
  }

  function goToPrev() {
    goToIndex(activeIndex - 1);
  }

  function goToIndex(index: number) {
    const maxIndex = images?.length ? numberOfSlides : 0;
    setActiveIndex(Math.min(Math.max(index, 0), maxIndex));
  }
};

export default ProductImageGallery;
